/**
 * Trails the user recorded and walks they made, with edit and delete.
 *
 * One read returns both lists. Walks can be on the user's own trails or on ones shared with them,
 * so a walk's trail is not necessarily in the trails list above it.
 */

import { useState } from 'react'
import { Chip, Typography } from '@mui/material'
import { Delete, Edit, Route, Terrain } from '@mui/icons-material'
import { useQuery } from '@tanstack/react-query'
import { DataTable, type DataTableColumn } from '@components/common/DataTable'
import type { RowAction } from '@components/common/RowActions'
import { userDataApi } from '@api/endpoints/userData'
import type { AdminUserTrailDto, AdminUserWalkDto } from '@api/types/userData'
import { EditTrailDialog, EditWalkDialog } from './UserDataEditDialogs'
import { UserDataActionDialog } from './UserDataActionDialog'
import { UserDataChangeResultDialog } from './UserDataChangeResultDialog'
import { UserDataSection } from './UserDataSection'
import { adminUserTrailsKey } from './userQueryKeys'
import { errorMessage, formatDate, formatDistance, formatDuration } from './userFormatting'
import { useTablePaging } from './useTablePaging'
import { useUserDataChange } from './useUserDataChange'
import { useIsAdmin } from './useIsAdmin'

type TrailAction =
  | { kind: 'edit-trail' | 'delete-trail'; trail: AdminUserTrailDto }
  | { kind: 'edit-walk' | 'delete-walk'; walk: AdminUserWalkDto }

/** Edit + Delete as row-action data: labelled on a phone card, Delete tucked in the menu. */
function editDeleteActions(label: string, onEdit: () => void, onDelete: () => void): RowAction[] {
  return [
    { key: 'edit', label: `Edit ${label}`, icon: <Edit fontSize="small" />, onClick: onEdit },
    { key: 'delete', label: `Delete ${label}`, icon: <Delete fontSize="small" />, color: 'error', onClick: onDelete },
  ]
}

export function UserTrailsSection({ userId }: { userId: string }) {
  const change = useUserDataChange(userId)
  // Moderators see this read-only; every write here is Admin-only on the API too.
  const isAdmin = useIsAdmin()
  const [action, setAction] = useState<TrailAction | null>(null)
  const trailPaging = useTablePaging()
  const walkPaging = useTablePaging()

  const { data, isLoading, error } = useQuery({
    queryKey: adminUserTrailsKey(userId),
    queryFn: () => userDataApi.getTrails(userId),
  })

  const loadError = error ? errorMessage(error, 'Failed to load trails and walks') : null

  const close = () => {
    setAction(null)
    change.resetError()
  }

  const trailColumns: DataTableColumn<AdminUserTrailDto>[] = [
    { key: 'name', header: 'Trail', primary: true, render: (t) => t.name || 'Untitled trail' },
    { key: 'recorded', header: 'Recorded', render: (t) => formatDate(t.recordedAt) },
    { key: 'distance', header: 'Distance', render: (t) => formatDistance(t.distanceMeters) },
    {
      key: 'duration',
      header: 'Duration',
      hideBelow: 'md',
      render: (t) => (t.durationMs != null ? formatDuration(t.durationMs / 1000) : '—'),
    },
    { key: 'steps', header: 'Steps', align: 'right', hideBelow: 'md', render: (t) => t.steps?.toLocaleString() ?? '—' },
    { key: 'walks', header: 'Walks', align: 'right', render: (t) => t.attemptCount.toLocaleString() },
  ]

  const walkColumns: DataTableColumn<AdminUserWalkDto>[] = [
    { key: 'trail', header: 'Trail', primary: true, render: (w) => w.trailName || 'Untitled trail' },
    {
      key: 'owner',
      header: 'Trail owner',
      hideBelow: 'md',
      render: (w) => <Chip size="small" variant="outlined" label={w.onOwnTrail ? 'Own trail' : 'Shared trail'} />,
    },
    { key: 'started', header: 'Started', render: (w) => formatDate(w.startedAt) },
    { key: 'elapsed', header: 'Time', render: (w) => formatDuration(w.elapsedMs / 1000) },
    { key: 'distance', header: 'Distance', render: (w) => formatDistance(w.distanceMeters) },
    { key: 'coverage', header: 'Coverage', align: 'right', hideBelow: 'md', render: (w) => `${Math.round(w.coveragePercent)}%` },
    { key: 'steps', header: 'Steps', align: 'right', hideBelow: 'lg', render: (w) => w.steps?.toLocaleString() ?? '—' },
  ]

  const mutate = (call: Parameters<typeof change.submit>[0]) => change.submit(call, close)

  return (
    <UserDataSection title="Trails and walks" icon={<Terrain fontSize="small" />}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Recorded trails
      </Typography>
      <DataTable
        rows={data?.trails ?? []}
        columns={trailColumns}
        getRowKey={(t) => t.id}
        loading={isLoading}
        error={loadError}
        emptyMessage="No recorded trails"
        dense
        {...trailPaging}
        getRowActions={(t) =>
          isAdmin
            ? editDeleteActions(
                'trail',
                () => setAction({ kind: 'edit-trail', trail: t }),
                () => setAction({ kind: 'delete-trail', trail: t })
              )
            : []
        }
      />

      <Typography variant="subtitle2" sx={{ mt: 3, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Route fontSize="small" /> Walks
      </Typography>
      <DataTable
        rows={data?.walks ?? []}
        columns={walkColumns}
        getRowKey={(w) => w.id}
        loading={isLoading}
        error={loadError}
        emptyMessage="No walks"
        dense
        {...walkPaging}
        getRowActions={(w) =>
          isAdmin
            ? editDeleteActions(
                'walk',
                () => setAction({ kind: 'edit-walk', walk: w }),
                () => setAction({ kind: 'delete-walk', walk: w })
              )
            : []
        }
      />

      {action?.kind === 'edit-trail' && (
        <EditTrailDialog
          key={action.trail.id}
          row={action.trail}
          pending={change.pending}
          error={change.error}
          onSubmit={(request) => mutate(() => userDataApi.editTrail(userId, action.trail.id, request))}
          onClose={close}
        />
      )}

      {action?.kind === 'delete-trail' && (
        <UserDataActionDialog
          title={`Delete the trail ${action.trail.name || 'Untitled trail'}?`}
          message={`Recorded ${formatDate(action.trail.recordedAt)}, ${formatDistance(action.trail.distanceMeters)}. Photos taken on it are kept. Distance and achievements are recalculated. This cannot be undone.`}
          confirmLabel="Delete trail"
          destructive
          pending={change.pending}
          error={change.error}
          onSubmit={(reason) => mutate(() => userDataApi.deleteTrail(userId, action.trail.id, reason))}
          onClose={close}
        />
      )}

      {action?.kind === 'edit-walk' && (
        <EditWalkDialog
          key={action.walk.id}
          row={action.walk}
          pending={change.pending}
          error={change.error}
          onSubmit={(request) => mutate(() => userDataApi.editWalk(userId, action.walk.id, request))}
          onClose={close}
        />
      )}

      {action?.kind === 'delete-walk' && (
        <UserDataActionDialog
          title="Delete this walk?"
          message={`${action.walk.trailName || 'Untitled trail'}, started ${formatDate(action.walk.startedAt)}: ${formatDuration(action.walk.elapsedMs / 1000)}, ${formatDistance(action.walk.distanceMeters)}. Distance and achievements are recalculated. This cannot be undone.`}
          confirmLabel="Delete walk"
          destructive
          pending={change.pending}
          error={change.error}
          onSubmit={(reason) => mutate(() => userDataApi.deleteWalk(userId, action.walk.id, reason))}
          onClose={close}
        />
      )}

      <UserDataChangeResultDialog open={change.resultOpen} result={change.result} onClose={change.closeResult} />
    </UserDataSection>
  )
}
